import { create } from 'zustand'

export interface UserProfile {
  name: string
  email: string
  bio: string
  avatar?: string
  company?: string
  location?: string
  website?: string
  timezone: string
}

export interface SecuritySettings {
  two_factor_enabled: boolean
  password_last_changed: string
  active_sessions: Array<{
    id: string
    device: string
    location: string
    last_active: string
    current: boolean
  }>
}

export interface NotificationSettings {
  email_notifications: {
    security_alerts: boolean
    server_alerts: boolean
    team_invitations: boolean
    marketing_updates: boolean
    weekly_digest: boolean
  }
  push_notifications: {
    server_down: boolean
    high_error_rate: boolean
    deployment_success: boolean
    team_mentions: boolean
  }
  alert_frequency: 'immediate' | 'hourly' | 'daily'
  quiet_hours: {
    enabled: boolean
    start: string // HH:MM format
    end: string   // HH:MM format
  }
}

export interface AppearanceSettings {
  theme: 'light' | 'dark' | 'system'
  language: string
  date_format: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD'
  time_format: '12h' | '24h'
  density: 'comfortable' | 'compact'
  sidebar_collapsed: boolean
}

export interface APISettings {
  api_keys: Array<{
    id: string
    name: string
    key: string
    permissions: string[]
    created_at: string
    last_used?: string
    expires_at?: string
  }>
  webhooks: Array<{
    id: string
    name: string
    url: string
    events: string[]
    active: boolean
    created_at: string
  }>
}

export interface PrivacySettings {
  profile_visibility: 'public' | 'team_only' | 'private'
  analytics_tracking: boolean
  data_sharing: boolean
  marketing_communications: boolean
  activity_tracking: boolean
}

export interface SystemPreferences {
  default_server_transport: 'http' | 'websocket' | 'sse' | 'stdio'
  auto_save_drafts: boolean
  confirmation_dialogs: boolean
  default_monitoring_interval: number // in seconds
  max_log_entries: number
}

interface SettingsStore {
  // State
  profile: UserProfile
  security: SecuritySettings
  notifications: NotificationSettings
  appearance: AppearanceSettings
  api: APISettings
  privacy: PrivacySettings
  system: SystemPreferences
  loading: boolean
  error: string | null

  // Actions
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>
  updateSecurity: (updates: Partial<SecuritySettings>) => Promise<void>
  updateNotifications: (updates: Partial<NotificationSettings>) => Promise<void>
  updateAppearance: (updates: Partial<AppearanceSettings>) => void
  updatePrivacy: (updates: Partial<PrivacySettings>) => Promise<void>
  updateSystem: (updates: Partial<SystemPreferences>) => void
  
  // API Key management
  createAPIKey: (name: string, permissions: string[]) => Promise<string>
  deleteAPIKey: (id: string) => Promise<void>
  regenerateAPIKey: (id: string) => Promise<string>
  
  // Webhook management
  createWebhook: (webhook: Omit<APISettings['webhooks'][0], 'id' | 'created_at'>) => Promise<void>
  updateWebhook: (id: string, updates: Partial<APISettings['webhooks'][0]>) => Promise<void>
  deleteWebhook: (id: string) => Promise<void>
  
  // Security actions
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  enableTwoFactor: () => Promise<{ qr_code: string; backup_codes: string[] }>
  disableTwoFactor: (code: string) => Promise<void>
  terminateSession: (sessionId: string) => Promise<void>
  terminateAllSessions: () => Promise<void>
  
  // Data management
  exportData: () => Promise<void>
  deleteAccount: (password: string) => Promise<void>
  
  // Utility
  loadSettings: () => Promise<void>
  resetSettings: () => void
}

const defaultProfile: UserProfile = {
  name: '',
  email: '',
  bio: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
}

const defaultSecurity: SecuritySettings = {
  two_factor_enabled: false,
  password_last_changed: new Date().toISOString(),
  active_sessions: []
}

const defaultNotifications: NotificationSettings = {
  email_notifications: {
    security_alerts: true,
    server_alerts: true,
    team_invitations: true,
    marketing_updates: false,
    weekly_digest: true
  },
  push_notifications: {
    server_down: true,
    high_error_rate: true,
    deployment_success: false,
    team_mentions: true
  },
  alert_frequency: 'immediate',
  quiet_hours: {
    enabled: false,
    start: '22:00',
    end: '08:00'
  }
}

const defaultAppearance: AppearanceSettings = {
  theme: 'system',
  language: 'en',
  date_format: 'MM/DD/YYYY',
  time_format: '12h',
  density: 'comfortable',
  sidebar_collapsed: false
}

const defaultAPI: APISettings = {
  api_keys: [],
  webhooks: []
}

const defaultPrivacy: PrivacySettings = {
  profile_visibility: 'team_only',
  analytics_tracking: true,
  data_sharing: false,
  marketing_communications: false,
  activity_tracking: true
}

const defaultSystem: SystemPreferences = {
  default_server_transport: 'http',
  auto_save_drafts: true,
  confirmation_dialogs: true,
  default_monitoring_interval: 30,
  max_log_entries: 1000
}

export const useSettingsStore = create<SettingsStore>((set, get) => ({
  // Initial state
  profile: defaultProfile,
  security: defaultSecurity,
  notifications: defaultNotifications,
  appearance: defaultAppearance,
  api: defaultAPI,
  privacy: defaultPrivacy,
  system: defaultSystem,
  loading: false,
  error: null,

  // Profile actions
  updateProfile: async (updates) => {
    set({ loading: true, error: null })
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500))
      
      set(state => ({
        profile: { ...state.profile, ...updates },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update profile',
        loading: false 
      })
      throw error
    }
  },

  updateSecurity: async (updates) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 500))
      
      set(state => ({
        security: { ...state.security, ...updates },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update security settings',
        loading: false 
      })
      throw error
    }
  },

  updateNotifications: async (updates) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 300))
      
      set(state => ({
        notifications: { ...state.notifications, ...updates },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update notifications',
        loading: false 
      })
      throw error
    }
  },

  updateAppearance: (updates) => {
    set(state => ({
      appearance: { ...state.appearance, ...updates }
    }))
    
    // Apply theme changes immediately
    if (updates.theme) {
      const root = document.documentElement
      if (updates.theme === 'dark') {
        root.classList.add('dark')
      } else if (updates.theme === 'light') {
        root.classList.remove('dark')
      } else {
        // System theme
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        if (prefersDark) {
          root.classList.add('dark')
        } else {
          root.classList.remove('dark')
        }
      }
    }
  },

  updatePrivacy: async (updates) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 300))
      
      set(state => ({
        privacy: { ...state.privacy, ...updates },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update privacy settings',
        loading: false 
      })
      throw error
    }
  },

  updateSystem: (updates) => {
    set(state => ({
      system: { ...state.system, ...updates }
    }))
  },

  // API Key management
  createAPIKey: async (name, permissions) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 500))
      
      const newKey = {
        id: `key_${Date.now()}`,
        name,
        key: `sk_${Math.random().toString(36).substr(2, 32)}`,
        permissions,
        created_at: new Date().toISOString()
      }
      
      set(state => ({
        api: {
          ...state.api,
          api_keys: [...state.api.api_keys, newKey]
        },
        loading: false
      }))
      
      return newKey.key
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to create API key',
        loading: false 
      })
      throw error
    }
  },

  deleteAPIKey: async (id) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 300))
      
      set(state => ({
        api: {
          ...state.api,
          api_keys: state.api.api_keys.filter(key => key.id !== id)
        },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to delete API key',
        loading: false 
      })
      throw error
    }
  },

  regenerateAPIKey: async (id) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 500))
      
      const newKey = `sk_${Math.random().toString(36).substr(2, 32)}`
      
      set(state => ({
        api: {
          ...state.api,
          api_keys: state.api.api_keys.map(key =>
            key.id === id ? { ...key, key: newKey } : key
          )
        },
        loading: false
      }))
      
      return newKey
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to regenerate API key',
        loading: false 
      })
      throw error
    }
  },

  // Webhook management
  createWebhook: async (webhook) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 500))
      
      const newWebhook = {
        ...webhook,
        id: `webhook_${Date.now()}`,
        created_at: new Date().toISOString()
      }
      
      set(state => ({
        api: {
          ...state.api,
          webhooks: [...state.api.webhooks, newWebhook]
        },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to create webhook',
        loading: false 
      })
      throw error
    }
  },

  updateWebhook: async (id, updates) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 300))
      
      set(state => ({
        api: {
          ...state.api,
          webhooks: state.api.webhooks.map(webhook =>
            webhook.id === id ? { ...webhook, ...updates } : webhook
          )
        },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update webhook',
        loading: false 
      })
      throw error
    }
  },

  deleteWebhook: async (id) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 300))
      
      set(state => ({
        api: {
          ...state.api,
          webhooks: state.api.webhooks.filter(webhook => webhook.id !== id)
        },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to delete webhook',
        loading: false 
      })
      throw error
    }
  },

  // Security actions
  changePassword: async (currentPassword, newPassword) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      set(state => ({
        security: {
          ...state.security,
          password_last_changed: new Date().toISOString()
        },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to change password',
        loading: false 
      })
      throw error
    }
  },

  enableTwoFactor: async () => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 800))
      
      const qrCode = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==`
      const backupCodes = Array.from({ length: 8 }, () => 
        Math.random().toString(36).substr(2, 8).toUpperCase()
      )
      
      set(state => ({
        security: {
          ...state.security,
          two_factor_enabled: true
        },
        loading: false
      }))
      
      return { qr_code: qrCode, backup_codes: backupCodes }
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to enable 2FA',
        loading: false 
      })
      throw error
    }
  },

  disableTwoFactor: async (code) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 500))
      
      set(state => ({
        security: {
          ...state.security,
          two_factor_enabled: false
        },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to disable 2FA',
        loading: false 
      })
      throw error
    }
  },

  terminateSession: async (sessionId) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 300))
      
      set(state => ({
        security: {
          ...state.security,
          active_sessions: state.security.active_sessions.filter(
            session => session.id !== sessionId
          )
        },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to terminate session',
        loading: false 
      })
      throw error
    }
  },

  terminateAllSessions: async () => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 500))
      
      set(state => ({
        security: {
          ...state.security,
          active_sessions: state.security.active_sessions.filter(
            session => session.current
          )
        },
        loading: false
      }))
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to terminate sessions',
        loading: false 
      })
      throw error
    }
  },

  // Data management
  exportData: async () => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const data = {
        profile: get().profile,
        settings: {
          notifications: get().notifications,
          appearance: get().appearance,
          privacy: get().privacy,
          system: get().system
        },
        exported_at: new Date().toISOString()
      }
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `openos-data-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      set({ loading: false })
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to export data',
        loading: false 
      })
      throw error
    }
  },

  deleteAccount: async (password) => {
    set({ loading: true, error: null })
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // This would typically redirect to login or show confirmation
      console.log('Account deletion requested')
      
      set({ loading: false })
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to delete account',
        loading: false 
      })
      throw error
    }
  },

  // Utility
  loadSettings: async () => {
    set({ loading: true, error: null })
    try {
      // Fetch current user from backend
      const { authApi } = await import('@/lib/api')
      const resp = await authApi.getCurrentUser()
      const user = (resp as any)?.user || null

      if (user) {
        set((state) => ({
          profile: {
            ...state.profile,
            name: user.name || state.profile.name || '',
            email: user.email || state.profile.email || '',
            // Keep existing optional fields if already set in local state
            bio: state.profile.bio || '',
            avatar: state.profile.avatar,
            company: state.profile.company,
            location: state.profile.location,
            website: state.profile.website,
            timezone: state.profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }))
      }

      set({ loading: false })
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load settings',
        loading: false 
      })
      throw error
    }
  },

  resetSettings: () => {
    set({
      profile: defaultProfile,
      security: defaultSecurity,
      notifications: defaultNotifications,
      appearance: defaultAppearance,
      api: defaultAPI,
      privacy: defaultPrivacy,
      system: defaultSystem,
      error: null
    })
  }
})) 